import CloseIcon from '@mui/icons-material/Close'
import { Box, Drawer, IconButton, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EntityPeekContext } from './peekContext'
import { PEEK_REGISTRY } from './registry'
import { formatPeek, PEEK_PARAM, parsePeek } from './relations'

/**
 * Hosts the peek panel: a side drawer showing whichever record an EntityLink
 * last opened. The open record lives in the URL (`?peek=type:id`), so a peek
 * can be shared or reloaded, following a link inside a peek is a normal
 * navigation, and the browser's Back button steps back through them.
 */
export default function EntityPeekProvider({ children }) {
    const [searchParams, setSearchParams] = useSearchParams()
    const current = parsePeek(searchParams.get(PEEK_PARAM))

    const open = useCallback(
        (type, id) =>
            setSearchParams((previous) => {
                const next = new URLSearchParams(previous)
                next.set(PEEK_PARAM, formatPeek(type, id))
                return next
            }),
        [setSearchParams]
    )
    const close = useCallback(
        () =>
            setSearchParams((previous) => {
                const next = new URLSearchParams(previous)
                next.delete(PEEK_PARAM)
                return next
            }),
        [setSearchParams]
    )

    const currentType = current?.type
    const currentId = current?.id
    const value = useMemo(
        () => ({
            current:
                currentType && currentId
                    ? { type: currentType, id: currentId }
                    : null,
            open,
            close
        }),
        [currentType, currentId, open, close]
    )

    const entry = current ? PEEK_REGISTRY[current.type] : null

    return (
        <EntityPeekContext.Provider value={value}>
            {children}
            <Drawer
                anchor="right"
                open={Boolean(current)}
                onClose={close}
                slotProps={{
                    paper: {
                        sx: { width: { xs: '100%', sm: 440 } },
                        role: 'dialog',
                        'aria-label': 'Record details'
                    }
                }}
            >
                <Box
                    sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
                >
                    <IconButton onClick={close} aria-label="Close details">
                        <CloseIcon />
                    </IconButton>
                </Box>
                {entry ? (
                    <entry.Peek
                        key={`${current.type}:${current.id}`}
                        type={current.type}
                        id={current.id}
                    />
                ) : current ? (
                    <Typography sx={{ p: 3, pr: 8 }} color="text.secondary">
                        This link points to something that can’t be shown here.
                    </Typography>
                ) : null}
            </Drawer>
        </EntityPeekContext.Provider>
    )
}

EntityPeekProvider.propTypes = {
    children: PropTypes.node
}
